// Phase 1A — single hardcoded property (your listing).
// Phase 2 will move this to Supabase keyed by host + slug.
//
// Scalability rule: the daily brief is the city's asset and must stay
// host-neutral. Everything property-specific (promo copy, hosts, the welcome
// guide overlay in welcome-guides.js) lives here and plugs INTO the brief;
// the brief never hardcodes a property.

import { reviewsSnapshot } from './reviews-snapshot.js';

export const properties = {
  // Slug pattern: property-named (the site brand is not a property). Renames
  // need a 301 in netlify.toml from the old slug — see the /stay/ redirects.
  'saltgrass-cottage': {
    slug: 'saltgrass-cottage',
    name: 'Saltgrass Cottage',
    hosts: 'James & Natalie',
    ownerRezPropertyId: null, // set via env on the server; UI doesn't need it
    headline: 'Book direct. Same home as on Airbnb and Vrbo, 3% off nightly rates and no service fees.',
    description:
      "Direct booking for our 3-bedroom home in Mount Pleasant, about 10 minutes from downtown Charleston and 15 from the Isle of Palms and Sullivan's Island beaches. Same calendar as Airbnb and Vrbo, 3% off nightly rates with no service fees, and your own concierge for the trip.",

    // SEO surface for the sales page (title tag, meta description, and the
    // visible H1), read by Stay.jsx and the prerender. Deliberately keyword-led
    // — the property NAME alone has no search volume, so the head term
    // ("Mount Pleasant vacation rental") leads and the brand rides along.
    seoTitle: 'Mount Pleasant Vacation Rental, Book Direct · Saltgrass Cottage',
    seoDescription:
      "3-bedroom Mount Pleasant vacation rental, 10 minutes to downtown Charleston and 15 to Isle of Palms and Sullivan's Island. Sleeps 8. Book direct: 3% off nightly rates, no service fees.",
    seoH1: 'Saltgrass Cottage: Mount Pleasant Vacation Rental',

    // ---- Hero (the masthead-grade header on the sales page). The kicker
    // carries the search keyword in small caps so the huge display line can be
    // just the name; the deck sells the place with concrete facts, never the
    // price (owner call 2026-07-28: leading with the cheapest night reads as
    // budget anchoring).
    heroKicker: 'A Mount Pleasant Vacation Rental',
    heroDeck:
      'A three-bedroom cottage on a quiet street in Mount Pleasant. Downtown Charleston is ten minutes over the bridge, and the beaches at Sullivan’s Island and Isle of Palms are fifteen.',
    heroDirectLine:
      'Book direct with your hosts: 3% off nightly rates, no service fees, and the same calendar as Airbnb and Vrbo.',
    // First sentence of the homepage promo module, in the host's voice.
    promoLine: 'This brief is written from the porch at Saltgrass Cottage, our home in Mount Pleasant.',
    airbnbUrl: 'https://www.airbnb.com/h/charlestonvacations',

    // ---- Photos. Files live at public/property/saltgrass-cottage/, each with a
    // -640 variant beside it for phone srcSet. Pulled from the Airbnb listing
    // 2026-07-24, so they are the web-optimized 1200w derivatives, not the
    // camera originals — re-export the originals from the Airbnb host dashboard
    // (Listings → Photos → download) when there is a reason to want 2x.
    //
    // GAP: the listing's exterior, covered porch, backyard, and fire-pit frames
    // were not in the served listing HTML and are NOT here. That matters more
    // than it looks: promoLine below sells "the porch," and the gallery cannot
    // currently show it. First frame to add when the host exports the full set.
    //
    // Order IS the gallery order. First entry is the lead frame and the
    // og:image source. Captions render under the lead and in the lightbox, so
    // they are guest-facing copy: concrete and specific, no marketing adjectives.
    // w/h are the intrinsic pixel dimensions of the full-size file, measured
    // from the JPEGs on 2026-09-21. They are not decoration: without them the
    // browser cannot reserve the frame before the bytes arrive, which is both
    // a layout shift and a slower largest paint. Re-measure on a re-shoot.
    images: [
      { file: 'living-room', w: 1200, h: 904, caption: 'The living room, stone hearth and vaulted ceiling.' },
      { file: 'entertainment-room-poker', w: 1200, h: 875, caption: 'The entertainment room, with the poker table set up.' },
      { file: 'kitchen', w: 1200, h: 875, caption: 'Full kitchen, island seating for three.' },
      { file: 'dining-area', w: 1200, h: 875, caption: 'The dining table, off the kitchen.' },
      { file: 'bedroom-king', w: 1200, h: 903, caption: 'Master bedroom: king bed, en suite bath, its own door to the yard.' },
      { file: 'bedroom-white-queen', w: 1200, h: 875, caption: 'The white guest bedroom, queen bed.' },
      { file: 'bedroom-white-queen-tv', w: 1200, h: 904, caption: 'The white guest bedroom, looking back toward the TV.' },
      { file: 'bedroom-blue-queen', w: 1200, h: 875, caption: 'The blue guest bedroom, queen bed.' },
      { file: 'bedroom-blue-queen-tv', w: 1200, h: 875, caption: 'The blue guest bedroom, second angle.' },
      { file: 'bathroom-marble', w: 701, h: 960, caption: 'Guest bath: marble tile, brass fixtures, walk-in shower.' },
      { file: 'bathroom-shower', w: 701, h: 960, caption: 'The walk-in shower, with a built-in bench.' },
      { file: 'entertainment-room-table', w: 1200, h: 875, caption: 'The entertainment room, table set for six.' },
      { file: 'entertainment-room-tv', w: 1200, h: 904, caption: 'The entertainment room sofa, which folds out to a queen.' },
      { file: 'arcade-cabinet', w: 722, h: 960, caption: 'The arcade cabinet, a few hundred games on it.' },
      { file: 'workstation', w: 1200, h: 904, caption: 'The workstation: full-size monitor and a real desk chair.' },
      { file: 'ev-charger', w: 723, h: 960, caption: 'Tesla wall charger, by the side door.' }
    ],
    bedrooms: 3,
    bathrooms: 2,
    // 8 = king + 2 queens + queen sleeper, 2 per bed. Matches the OwnerRez
    // property capacity and the rental agreement (confirmed 2026-07-10) —
    // keep all three in sync if the sleeping setup changes.
    sleeps: 8,
    // OwnerRez enforces three separate caps, and only the total was configured
    // here, so the booking form offered up to 8 adults and 7 children against
    // a property that refuses either. Confirmed against live quotes on
    // 2026-09-21 (Dec 8 to 11): 6 adults and 5 children both price, 7 adults
    // returns "This property allows a maximum of 6 adults.", 6 children
    // returns "a maximum of 5 children.", and 6 + 3 returns "a maximum of 8
    // guests." Keep these three in sync with the OwnerRez property record;
    // they are its rules, not ours.
    maxAdults: 6,
    maxChildren: 5,
    minNights: 2,
    maxNights: 30,
    location: 'Mount Pleasant, SC',

    // Check-in / check-out, copied from the welcome guide's house-facts block
    // (welcome-guides.js) — keep the two in sync. 24-hour clock because these
    // feed schema.org checkinTime/checkoutTime and the machine-readable
    // /api/stay/<slug> payload, where "after 3:00 PM" is not parseable.
    checkinTime: '15:00',
    checkoutTime: '10:00',

    // The full address. NOT a public field any more: owner decision
    // 2026-09-21 is that the machine surfaces publish locality plus a
    // coordinate rounded to about a kilometre, and the exact address goes to
    // the guest with the check-in details, which is what the welcome guide
    // has always promised and what Airbnb and Vrbo do. `street` is read by
    // nothing public; postalAddress() in property-schema.js omits it unless
    // asked for it explicitly.
    //
    // One thing this does NOT cover: `mapQuery` below still pins the embedded
    // neighbourhood map on the house, per the separate 2026-07-22 decision
    // that the sales page shows the marker. Anyone reading the page source
    // can recover the address from it. Changing that is a one-line edit here
    // and a different call from this one.
    address: {
      street: '1610 Nantahala Blvd',
      locality: 'Mount Pleasant',
      region: 'SC',
      postalCode: '29464',
      country: 'US'
    },
    // Read off OwnerRez property 480227 (2026-08-01), which is the source of
    // truth for both. Coordinates let an answer engine place the house against
    // "how far to the beach" without geocoding a string; approximateGeo() in
    // property-schema.js rounds them before anything public sees them.
    geo: { latitude: 32.826033, longitude: -79.840217 },

    // Minimum age to book, matching rental agreement §2 (owner call
    // 2026-08-01). The OwnerRez property rule feeds {PMINAGE} in the signed
    // agreement and is set in the dashboard, not over the API (the Properties
    // endpoints are read-only) — keep this number and that setting in sync.
    minRenterAge: 25,

    // ---- Revenue dashboard (/admin/stay/<slug>/revenue). Per-listing inputs
    // the engine reads at runtime. R1: econ moved out of the engine to here.
    // cleaningFee = what a DIRECT guest pays (revenue in; an Airbnb guest pays
    // the higher grossed-up figure above, which is offset by Airbnb's host
    // fee); cleaningCost = what the cleaner charges the host, all-in (cost
    // out, confirmed $115.50 2026-07-10);
    // hostFee = effective channel fee %. Phase 2 moves these to Supabase.
    revenue: {
      priceLabs: { listingId: '480227', pms: 'ownerrez' },
      bedrooms: 3, // which neighborhood bedroom-category to pull (Category "3")
      econ: { cleaningCost: 115.5, cleaningFee: 150, hostFee: 15.5 },
      subtitle: "Coastal 3BR · 10 min to IoP & Sullivan's · Mount Pleasant, SC",
    },

    // ---- Sales-page content (rendered by Stay.jsx). Compact summaries of
    // facts that live in long form in welcome-guides.js — keep the two in
    // sync when the house changes.

    // Rate anchors for the "What do nightly rates run?" FAQ (visible page,
    // FAQPage JSON-LD, and the prerendered static HTML AI crawlers read).
    // Deliberately NOT rendered in the hero: leading with the cheapest night
    // read as budget anchoring (owner call, 2026-07-28). fromNightly = real
    // slow-season nightly net of the direct discount: $145 PriceLabs floor
    // × 1.1834 channel-parity offset × 0.97 (direct = Airbnb − 3%, owner call
    // 2026-09-20; was × 0.95 at 5% from 2026-07-29). peakNightly =
    // owner-stated peak-season nightly (2026-07-28). Recheck when rates move;
    // the quote is always the source of truth for real dates.
    fromNightly: 166,
    peakNightly: 450,

    // Guest-facing cleaning fee, per channel. Split into two OwnerRez
    // surcharges on 2026-08-01: 102700420 ($150, every channel but Airbnb) and
    // 102724260 ($177.51, Airbnb only). The Airbnb figure carries the 15.5%
    // host fee Airbnb charges us; a direct booking has no such fee to cover,
    // so direct guests save the difference on top of the direct discount on rent. Keep
    // these two numbers in step with those surcharges: the FAQ, the facts
    // endpoint, and the savings math all read them from here. The quote is
    // still the authority for any real set of dates.
    cleaningFee: 150,
    cleaningFeeAirbnb: 177.51,

    // Lodging tax on rent + cleaning, as a percent: the five OwnerRez taxes
    // (SC sales 5, SC accommodations 2, county sales 3, county accommodations
    // 2, town accommodations 1) verified 2026-07-27. Airbnb collects the same
    // rate on its own bookings and applies it to its service fee too. The
    // quote is the authority for any real dates; this figure feeds the facts
    // endpoint and the FAQ's worked example only.
    taxRatePercent: 13,

    // Host block. hostPhoto renders when a real file lands at the path.
    hostPhoto: null, // e.g. '/property/saltgrass-cottage/hosts.jpg'
    hostIntro:
      "Saltgrass Cottage is our home in Mount Pleasant, and the daily brief on this site started as the morning note we kept for its guests. Book direct and you deal with us, not a platform inbox: you'll have our cell from booking to checkout.",

    // Guest reviews: the rating and count that /stay, the /stay card, the
    // JSON-LD, the prerender and /api/stay all print. Never invent these.
    //
    // These are the hand-entered fallback AND the floor. Once the site is
    // connected to OwnerRez (/api/ownerrez-connect), every build replaces them
    // with OwnerRez's figures across all its channels (scripts/sync-reviews.mjs),
    // but only while OwnerRez has at least this many reviews. Read off the
    // Airbnb listing 2026-10-02 ("Rated 4.98 out of 5 from 44 reviews"); before
    // that the page said 4.97 across 32 from 2026-07-24 and nobody noticed it
    // drift, which is why this is no longer maintained by hand.
    //
    // `quotes` stays empty until real review text is copied across. The
    // Reviews block renders the rating alone rather than waiting on quotes.
    reviews: {
      rating: 4.98,
      count: 44,
      sites: [{ name: 'Airbnb', count: 44, rating: 4.98 }],
      asOf: '2026-10-02',
      checkedVia: 'listing',
      quotes: [] // e.g. { text, guestFirstName, date: 'March 2026' }
    },

    // Cancellation refund tiers, most generous first. Policy locked 2026-07-10
    // (rental agreement §5, legal in SC per § 27-50-240(B)): non-refundable,
    // stated plainly rather than hidden until checkout.
    cancellationTiers: [
      {
        window: 'Any time after booking',
        refund: 'Non-refundable. We recommend travel insurance for peace of mind.'
      }
    ],

    beds: [
      { room: 'Master bedroom', bed: 'King bed, en suite bath' },
      { room: 'Guest bedroom (white)', bed: 'Queen bed' },
      { room: 'Guest bedroom (blue)', bed: 'Queen bed' },
      { room: 'Entertainment room', bed: 'Queen pull-out couch' }
    ],
    // Added 2026-07-24 from the listing photos: the arcade cabinet, poker
    // table, and Tesla wall charger were all visible in the gallery but missing
    // from this list. The charger in particular is a real booking
    // differentiator, so it leads.
    amenities: [
      'Tesla wall charger on site (free to use)',
      'Full kitchen with coffee maker, K-cups, blender & crockpot',
      'Arcade cabinet with a few hundred games',
      'Poker table in the entertainment room',
      'Smart TV in every bedroom plus the entertainment room',
      'Wi-Fi throughout (details in your check-in message)',
      'Workstation with monitor for remote work',
      'Screened-in porch',
      'Washer & dryer',
      'Pack & play, high chair, sound machine & kids’ dishes',
      'Games, kids’ books & toys',
      'En suite master bath plus full guest bath',
      'Hair dryers & hotel-style linens in every room'
    ],

    // Neighborhood paragraph + the distances list from the welcome guide.
    // Map marker points at the exact house: the owner chose to publish the
    // property's street address on the public sales page (2026-07-22),
    // overriding the earlier town-level-only default. WiFi and lockbox codes
    // stay private and are still never published (see welcome-guides.js).
    neighborhood:
      "Saltgrass Cottage sits on a quiet residential street in Mount Pleasant, the coastal town just across the Ravenel Bridge from downtown Charleston. Downtown and the historic peninsula are about 10 minutes away; the beaches at Sullivan's Island and Isle of Palms are about 15. In between you have Shem Creek (shrimp boats, dolphins, and the waterfront restaurants), the Old Village, and Patriots Point. Trader Joe's is 1.3 miles from the door, with Mount Pleasant Towne Centre close by for anything you forget to pack.",
    mapQuery: '1610 Nantahala Blvd, Mount Pleasant, SC 29464',
    distances: [
      { place: 'Isle of Palms', distance: '5.5 miles' },
      { place: "Sullivan's Island", distance: 'under 6 miles' },
      { place: 'Downtown Charleston', distance: 'under 7 miles' }
    ]
  }
};

// The hand-entered review numbers, kept before the synced ones are laid over
// them: they are the floor a sync has to meet (checkSync in review-summary.js).
const HAND_REVIEWS = Object.fromEntries(
  Object.entries(properties).map(([slug, p]) => [slug, p.reviews || null])
);

export function handReviews(slug) {
  return HAND_REVIEWS[slug] || null;
}

// Numbers the build read from OwnerRez and accepted. A held or unavailable
// sync leaves the hand-entered block as it is.
for (const [slug, synced] of Object.entries(reviewsSnapshot.properties || {})) {
  const property = properties[slug];
  if (!property || synced?.status !== 'synced') continue;
  property.reviews = {
    ...property.reviews,
    rating: synced.rating,
    count: synced.count,
    sites: synced.sites,
    asOf: synced.asOf,
    checkedVia: 'ownerrez'
  };
}

export function getProperty(slug) {
  return properties[slug] || null;
}

// Expand the compact `images` entries into the shapes the UI and the schema.org
// payload want. Kept here rather than in the Gallery so the prerender and the
// JSON-LD builders resolve identical paths without importing a component.
export function propertyPhotos(property) {
  if (!property?.images?.length) return [];
  const base = `/property/${property.slug}`;
  return property.images.map((img) => ({
    src: `${base}/${img.file}.jpg`,
    src640: `${base}/${img.file}-640.jpg`,
    width: img.w,
    height: img.h,
    caption: img.caption,
    // Captions are written as descriptions of the frame, so they double as alt
    // text; an explicit `alt` on the entry overrides when the two should differ.
    alt: img.alt || img.caption
  }));
}

// Absolute URLs for schema.org `image` (Google wants absolute, and wants the
// lead frame first).
export function propertyPhotoUrls(property, origin) {
  return propertyPhotos(property).map((p) => `${origin}${p.src}`);
}

// The property currently occupying the homepage promo slot and the nav's
// Stay link. With one host this is trivially the only property; when more
// join, this becomes a rotation or market-level choice without the brief
// pages changing.
export function getFeaturedProperty() {
  const slugs = Object.keys(properties);
  return slugs.length ? properties[slugs[0]] : null;
}
